import {
  useCallback, useEffect, useReducer, useState,
} from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

const APPLY_CART = 'applyCart'
const UPDATE_CART_ITEM_IDS = 'updateCartItemIds'

const INITIAL_CART_STATE = { cart: EMPTY_OBJECT, cartItemIds: EMPTY_ARRAY }

// Read through usePageConfig rather than a window-config-loaded listener: useSyncExternalStore
// commits synchronously, while a listener's setState lands in a second, default-priority commit -
// so the viewer state used to re-render the grid once more right after Results' own config read.
const selectViewerConfig = (config) => ({
  currentUserId: config?.currentUserId || undefined,
  viewerIsAdult: Boolean(config?.viewerIsAdult),
  showCgtVerification: Boolean(config?.showCgtVerification),
  cart: config?.cart || EMPTY_OBJECT,
})

// The navbar re-broadcasts the cart object pageConfig already published; re-applying an unchanged
// cart would drop items a card added to cartItemIds since, so it is a no-op.
const cartReducer = (state, action) => {
  if (action.type === APPLY_CART) {
    const cart = action.cart || EMPTY_OBJECT
    if (cart === state.cart) {
      return state
    }
    return { cart, cartItemIds: cart.cartItemIds || EMPTY_ARRAY }
  }

  const cartItemIds = typeof action.update === 'function' ? action.update(state.cartItemIds) : action.update
  return cartItemIds === state.cartItemIds ? state : { ...state, cartItemIds }
}

// Client-side cart / wishlist / viewer state for the results grid, mirroring ListingPage's
// List.jsx. On fast pages (SearchPagesController) this data is never in the SSR props - it is
// published client-side through window.pageConfig and the shared cart events, so the ModelCard's
// own add/remove handlers (App.events, /api/internal/*) work unchanged.
const useCartWishlist = () => {
  const viewerConfig = usePageConfig({ select: selectViewerConfig })
  const [{ cart, cartItemIds }, dispatchCart] = useReducer(cartReducer, INITIAL_CART_STATE)
  const [wishListItems, setWishListItems] = useState(EMPTY_ARRAY)
  const [confirmedViewerIsAdult, setViewerIsAdult] = useState(undefined)
  const [appliedConfigCart, setAppliedConfigCart] = useState(EMPTY_OBJECT)

  // Applied during render so the published cart lands in the same commit as the viewer flags.
  if (viewerConfig.cart !== appliedConfigCart) {
    setAppliedConfigCart(viewerConfig.cart)
    dispatchCart({ type: APPLY_CART, cart: viewerConfig.cart })
  }

  const setCartItemIds = useCallback((update) => {
    dispatchCart({ type: UPDATE_CART_ITEM_IDS, update })
  }, [])

  useEffect(() => {
    const setCartInfo = (event) => {
      dispatchCart({ type: APPLY_CART, cart: event.detail })
    }

    const removeCartItemId = () => {
      const currentCartItemIds = (window?.pageConfig?.cart_items || []).map(({ id }) => id)
      setCartItemIds(state => {
        const itemToBeRemoved = state.find(({ itemId }) => !currentCartItemIds.includes(itemId))
        return state.filter(({ itemId }) => itemId !== itemToBeRemoved?.itemId)
      })
    }

    window.addEventListener('navigation-cart-loaded', setCartInfo)
    window.addEventListener('item-removed-from-cart', removeCartItemId)

    return () => {
      window.removeEventListener('navigation-cart-loaded', setCartInfo)
      window.removeEventListener('item-removed-from-cart', removeCartItemId)
    }
  }, [setCartItemIds])

  const { currentUserId } = viewerConfig

  useEffect(() => {
    if (!currentUserId) {
      return undefined
    }

    let active = true
    const getWishListItems = async () => {
      try {
        const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')
        // The endpoint serializes wishlist *items* with RecentItemSerializer (type "recentItems"),
        // whose attributes are { id, title, price, url, imageThumb } - there is no itemId, so the
        // long-standing `attributes.itemId` read here always yielded an empty list and no card
        // ever showed as wishlisted on load. Note free items are excluded server-side
        // (WishListItemsController#render_wish_list filters `price != 0`), so they never seed.
        const ids = (wishlist?.data || [])
          .map((item) => Number(item?.attributes?.id))
          .filter(Boolean)
        if (active && ids.length > 0) {
          setWishListItems(ids)
        }
      } catch (error) {
        // Wishlist seeding is best-effort; add/remove still works without the initial list.
      }
    }

    getWishListItems()

    return () => {
      active = false
    }
  }, [currentUserId])

  return {
    cart,
    cartItemIds,
    setCartItemIds,
    wishListItems,
    setWishListItems,
    currentUserId,
    viewerIsAdult: confirmedViewerIsAdult ?? viewerConfig.viewerIsAdult,
    setViewerIsAdult,
    showCgtVerification: viewerConfig.showCgtVerification,
  }
}

export default useCartWishlist
